import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';

import { ALL_CASINGS, Casing } from 'src/app/models/filename-conventions/casing';
import {
  FilenameConventionPayload,
  FilenameConventionRangeBased,
} from 'src/app/models/filename-conventions/filename-convention-payload';
import { FilenameConventionRangeEntry } from 'src/app/models/filename-conventions/filename-convention-range-entry';
import { TokenFormat } from 'src/app/models/filename-conventions/token-format';
import {
  FilenameConventionPreviewService,
  RenderResult,
} from 'src/app/services/filename-conventions/filename-convention-preview.service';
import { casingLongLabel } from '../../shared/convention-labels';
import { FILENAME_TOKEN_VOCABULARY_WITH_CUSTOM } from '../../shared/token-chip-picker/token-vocabulary';

interface RangeEntryForm {
  from: FormControl<number>;
  to: FormControl<number | null>;
  indexOffset: FormControl<number>;
  tokenFormats: FormControl<TokenFormat[]>;
  casing: FormControl<Casing>;
}

interface RangeFormShape {
  entries: FormArray<FormGroup<RangeEntryForm>>;
}

const SAMPLE_TOKEN_VALUES: Record<string, string> = {
  barcode: 'SAMPLE-BARCODE-123',
  brand: 'Sample Brand',
  productName: 'Sample Product',
  reference: 'REF-42',
  colour: 'Blue',
  index: '1',
  description: 'sample-desc',
  extension: 'jpg',
  'alphabetical-index': 'a',
};

const CASING_ICONS: Record<Casing, string> = {
  Match: 'check',
  Upper: 'arrow_upward',
  Lower: 'arrow_downward',
};

const CASING_LABELS: Record<Casing, string> = {
  Match: 'Aa',
  Upper: 'A',
  Lower: 'a',
};

const DEFAULT_RANGE_CREATE_TOKEN_FORMATS: readonly TokenFormat[] = [
  { order: 0, value: '{reference}', origin: 'Template' },
  { order: 1, value: '_', origin: 'Template' },
  { order: 2, value: '{index}', origin: 'Template' },
  { order: 3, value: '.', origin: 'Template' },
  { order: 4, value: '{extension}', origin: 'Template' },
];

@Component({
  selector: 'app-range-filename-convention-editor',
  templateUrl: './range-convention-editor.component.html',
  styleUrls: ['./range-convention-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class RangeFilenameConventionEditorComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly previewService = inject(FilenameConventionPreviewService);

  @Input() value: FilenameConventionPayload | null = null;
  @Input() organizationId: string = '';
  @Output() readonly save = new EventEmitter<FilenameConventionPayload>();

  readonly allCasings = ALL_CASINGS;
  readonly tokenVocabulary = FILENAME_TOKEN_VOCABULARY_WITH_CUSTOM;
  readonly casingIcon = (c: Casing): string => CASING_ICONS[c];
  readonly casingLabel = (c: Casing): string => CASING_LABELS[c];
  readonly casingTitle = (c: Casing): string => casingLongLabel(c);

  rangeForm!: FormGroup<RangeFormShape>;

  ngOnInit(): void {
    this.rangeForm = this.buildRangeForm(this.value?.mode === 'rangeBased' ? this.value : null);
    this.subscribeFormChanges();
  }

  get rangeEntries(): FormArray<FormGroup<RangeEntryForm>> {
    return this.rangeForm.controls.entries;
  }

  addRangeEntry(): void {
    this.rangeEntries.push(this.buildRangeEntryGroup(this.blankRangeEntry()));
  }

  removeRangeEntry(index: number): void {
    this.rangeEntries.removeAt(index);
  }

  onTokensChange(index: number, tokens: TokenFormat[]): void {
    const group = this.rangeEntries.at(index);
    if (group === undefined) return;
    group.controls.tokenFormats.setValue(tokens);
  }

  entryPreview(index: number): RenderResult {
    const group = this.rangeEntries.at(index);
    if (group === undefined) return { value: null, error: null };
    const entry = this.readRangeEntry(group);
    const singleEntryPayload: FilenameConventionRangeBased = {
      mode: 'rangeBased',
      entries: [entry],
    };
    const sampleIndex = Math.max(entry.from, 1);
    return this.previewService.renderRangeBased(
      singleEntryPayload,
      sampleIndex,
      SAMPLE_TOKEN_VALUES,
    );
  }

  entryPreviewSecondary(index: number): RenderResult {
    const group = this.rangeEntries.at(index);
    if (group === undefined) return { value: null, error: null };
    const entry = this.readRangeEntry(group);
    const singleEntryPayload: FilenameConventionRangeBased = {
      mode: 'rangeBased',
      entries: [entry],
    };
    const secondary = Math.max(entry.from + 1, 2);
    if (entry.to !== undefined && secondary > entry.to) {
      return { value: null, error: null };
    }
    return this.previewService.renderRangeBased(
      singleEntryPayload,
      secondary,
      SAMPLE_TOKEN_VALUES,
    );
  }

  private subscribeFormChanges(): void {
    this.rangeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.save.emit(this.buildRangePayload());
      });
  }

  private buildRangePayload(): FilenameConventionRangeBased {
    return {
      mode: 'rangeBased',
      entries: this.rangeEntries.controls.map((group) => this.readRangeEntry(group)),
    };
  }

  private readRangeEntry(group: FormGroup<RangeEntryForm>): FilenameConventionRangeEntry {
    const to = group.controls.to.value;
    const base: FilenameConventionRangeEntry = {
      from: group.controls.from.value,
      indexOffset: group.controls.indexOffset.value,
      tokenFormats: this.normaliseOrder(group.controls.tokenFormats.value),
      casing: group.controls.casing.value,
    };
    return to === null ? base : { ...base, to };
  }

  private normaliseOrder(tokens: readonly TokenFormat[]): TokenFormat[] {
    return tokens.map((token, index) =>
      token.origin === undefined
        ? { order: index, value: token.value }
        : { order: index, value: token.value, origin: token.origin },
    );
  }

  private buildRangeForm(seed: FilenameConventionRangeBased | null): FormGroup<RangeFormShape> {
    const entryGroups = seed === null
      ? [this.buildRangeEntryGroup(this.defaultCreateRangeEntry())]
      : seed.entries.map((entry) => this.buildRangeEntryGroup(entry));
    return this.fb.group<RangeFormShape>({
      entries: this.fb.array(entryGroups),
    });
  }

  private defaultCreateRangeEntry(): FilenameConventionRangeEntry {
    return {
      from: 1,
      indexOffset: 0,
      tokenFormats: DEFAULT_RANGE_CREATE_TOKEN_FORMATS.map((t) => ({ ...t })),
      casing: 'Match',
    };
  }

  private buildRangeEntryGroup(entry: FilenameConventionRangeEntry): FormGroup<RangeEntryForm> {
    return this.fb.group<RangeEntryForm>({
      from: this.fb.control(entry.from, [Validators.required]),
      to: new FormControl<number | null>(entry.to ?? null),
      indexOffset: this.fb.control(entry.indexOffset),
      tokenFormats: this.fb.control<TokenFormat[]>([...entry.tokenFormats]),
      casing: this.fb.control(entry.casing),
    });
  }

  private blankRangeEntry(): FilenameConventionRangeEntry {
    return {
      from: 1,
      indexOffset: 0,
      tokenFormats: [],
      casing: 'Match',
    };
  }
}
